import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, Truck, X } from "lucide-react";
import { FLAVORS, PRICE, FREE_SHIPPING_THRESHOLD, formatPrice } from "../data/products";
import { useStore } from "../context/StoreContext";

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const SHOPIFY_VARIANTS = {
  tropical: process.env.REACT_APP_SHOPIFY_VARIANT_TROPICAL,
  apple: process.env.REACT_APP_SHOPIFY_VARIANT_APPLE,
};

const CartDrawer = () => {
  const { t, cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartTotal } = useStore();
  const close = () => setCartOpen(false);

  const goShopifyCheckout = () => {
    const parts = cart.map((i) => `${SHOPIFY_VARIANTS[i.flavor]}:${i.qty}`).join(",");
    window.location.href = `https://${SHOPIFY_DOMAIN}/cart/${parts}`;
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close} className="fixed inset-0 z-[80] bg-black/40" data-testid="cart-overlay" />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[85] flex h-full w-full max-w-md flex-col border-l border-[var(--line)] bg-[var(--bg)]"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-[var(--line)] p-5">
              <h3 className="font-display text-3xl" data-testid="cart-title">{t.cart.title}</h3>
              <button data-testid="cart-close-button" onClick={close} className="text-[var(--muted)] hover:text-[var(--ink)]" aria-label="close">
                <X className="h-6 w-6" />
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center" data-testid="cart-empty">
                <p className="text-sm text-[var(--muted)]">{t.cart.empty}</p>
                <button onClick={close} className="btn-outline px-6 py-2.5 text-sm" data-testid="continue-shopping-button">{t.cart.continue}</button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-5">
                  <div className="space-y-3">
                    {cart.map((item) => {
                      const f = FLAVORS[item.flavor];
                      return (
                        <div key={item.flavor} className="card flex gap-4 p-3" data-testid={`cart-item-${item.flavor}`}>
                          <img src={f.image} alt={f.name} className="h-20 w-20 object-cover" />
                          <div className="flex flex-1 flex-col justify-between">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-bold">ZENKAI · {f.name}</p>
                                <p className="text-xs text-[var(--muted)]">{formatPrice(PRICE)}</p>
                              </div>
                              <button data-testid={`remove-${item.flavor}`} onClick={() => removeFromCart(item.flavor)} className="text-[var(--muted)] hover:text-[var(--ink)]" aria-label="remove">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center border border-[var(--line)] bg-white">
                                <button data-testid={`cart-minus-${item.flavor}`} onClick={() => updateQty(item.flavor, -1)} className="p-1.5" aria-label="-"><Minus className="h-3.5 w-3.5" /></button>
                                <span className="w-8 text-center text-sm font-bold" data-testid={`cart-qty-${item.flavor}`}>{item.qty}</span>
                                <button data-testid={`cart-plus-${item.flavor}`} onClick={() => updateQty(item.flavor, 1)} className="p-1.5" aria-label="+"><Plus className="h-3.5 w-3.5" /></button>
                              </div>
                              <span className="font-display text-xl" data-testid={`cart-line-total-${item.flavor}`}>{formatPrice(item.qty * PRICE)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-[var(--line)] bg-[var(--paper)] p-5">
                  <div className="mb-4" data-testid="free-shipping-bar">
                    <p className="mb-2 flex items-center gap-2 text-xs font-semibold" data-testid="free-shipping-label">
                      <Truck className="accent-text h-4 w-4" />
                      {cartTotal >= FREE_SHIPPING_THRESHOLD ? t.cart.shippingFree : t.cart.shippingAway.replace("{amount}", formatPrice(FREE_SHIPPING_THRESHOLD - cartTotal))}
                    </p>
                    <div className="h-1.5 w-full bg-[var(--line)]">
                      <motion.div animate={{ width: `${Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }} transition={{ duration: 0.4 }} className="accent-bg h-full" data-testid="free-shipping-progress" />
                    </div>
                  </div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm text-[var(--muted)]">{t.cart.subtotal}</span>
                    <span className="font-display text-3xl" data-testid="cart-subtotal">{formatPrice(cartTotal)}</span>
                  </div>
                  <p className="mb-4 text-xs text-[var(--muted)]">{t.cart.shippingNote}</p>
                  <button data-testid="go-checkout-button" onClick={goShopifyCheckout} className="btn-primary w-full py-3.5 text-base">
                    {t.cart.checkout}
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
